import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Autocomplete
} from "@mui/material";

import axios from "../../../utils/axios";
import Swal from "sweetalert2";
import { useForm, Controller } from "react-hook-form";

const FdrCreateModal = ({ open, handleClose, refreshList }) => {

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      
      refNumber: "",
      fdrNumber: "",
      startDate: "",
      maturityDate: "",
      bankName: null,
      branchName: null,
      accountNo: "",
      principalAmount: "",
      interestRate: "",
      interestBasis: "YEARLY",
    }
  });

  const clearForm = () => {
  reset({
    refNumber: "",
    fdrNumber: "",
    startDate: "",
    maturityDate: "",
    bankName: null,
    branchName: null,
    accountNo: "",
    principalAmount: "",
    interestRate: "",
    interestBasis: "YEARLY",
  });

  setFormData({
    bankName: null,
    branch: null,
  });

  setBranches([]);
};

  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);

  const [formData, setFormData] = useState({
    bankName: null,
    branch: null,
  });

  // Load Banks
  useEffect(() => {
    axios.get("/bank/get-all-bank-names")
      .then((res) => {
        if (res.data.success) {
          setBanks(res.data.payload);
        }
      })
      .catch(() => Swal.fire("Error", "Failed to load banks", "error"));
  }, []);

  // Bank Change
  const handleBankChange = async (event, value) => {

    setFormData({
      bankName: value,
      branch: null
    });

    setBranches([]);

    if (value) {
      try {
        const res = await axios.post("/bank/get-all-banks-by-name", {
          bankName: value.bankName
        });

        if (res.data.success) {
          setBranches(res.data.payload);
        }
      } catch {
        Swal.fire("Error", "Failed to load branches", "error");
      }
    }
  };

  // Branch Change
  const handleBranchChange = (event, value) => {
    setFormData({
      ...formData,
      branch: value
    });
  };

  // Submit
  const onSubmit = async (values) => {

    if (!formData.bankName || !formData.branch) {
      Swal.fire({
        customClass: {
          container: 'my-swal'
        },
        icon: 'warning',
        title: 'Please select bank and branch',
        showConfirmButton: true,
        // timer: 3000
      });
      return;
    }
    if (!values.fdrNumber) {
        Swal.fire({
        customClass: {
          container: 'my-swal'
        },
        icon: 'warning',
        title: 'Please enter FDR number',
        showConfirmButton: true,
        // timer: 3000
      });
      return;
    }

    const payload = {
      refNumber: values.refNumber,
      bankId: formData.branch.id,
      fdrNumber: values.fdrNumber,
      principalAmount: values.principalAmount,
      interestRate: values.interestRate,
      interestBasis: values.interestBasis,
      startDate: values.startDate,
      maturityDate: values.maturityDate
    };

    try {

      const res = await axios.post("/fdr", payload);

   if (res.data.success) {

    Swal.fire({
        customClass: {
          container: 'my-swal'
        },
        icon: 'success',
        title: 'FDR Created Successfully',
        showConfirmButton: true,
        // timer: 3000
      });

  clearForm();
  handleClose();
  refreshList();
}

    } catch {
      Swal.fire({
        customClass: {
          container: 'my-swal'
        },
        icon: 'error',
        title: 'Failed to create FDR',
        showConfirmButton: true,
        // timer: 3000
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>

      <DialogTitle>Add New FDR</DialogTitle>

      <DialogContent>

        <form id="fdrForm" onSubmit={handleSubmit(onSubmit)}>

          <Grid container spacing={2} mt={1}>

            <Grid item xs={6}>
              <Controller
                name="refNumber"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Reference Number" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="fdrNumber"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="FDR Number" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="maturityDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    label="Maturity Date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                )}
              />
            </Grid>

            {/* Bank */}
            <Grid item xs={6}>
              <Autocomplete
                options={banks}
                getOptionLabel={(option) => option.bankName || ""}
                value={formData.bankName}
                onChange={handleBankChange}
                renderInput={(params) => (
                  <TextField {...params} label="Bank Name" fullWidth />
                )}
              />
            </Grid>

            {/* Branch */}
            <Grid item xs={6}>
              <Autocomplete
                options={branches}
                getOptionLabel={(option) =>
                  option.branchName
                    ? `${option.branchName} - ${option.routingNo}`
                    : ""
                }
                value={formData.branch}
                onChange={handleBranchChange}
                disabled={!formData.bankName}
                renderInput={(params) => (
                  <TextField {...params} label="Branch Name" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="principalAmount"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Principal Amount" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="interestRate"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Interest Rate (%)" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="interestBasis"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label="Interest Basis" fullWidth>
                    <MenuItem value="YEARLY">Yearly</MenuItem>
                    <MenuItem value="MONTHLY">Monthly</MenuItem>
                    <MenuItem value="DAILY">Daily</MenuItem>
                  </TextField>
                )}
              />
            </Grid>

          </Grid>

        </form>

      </DialogContent>

      <DialogActions>
       <Button
                onClick={() => {
                    clearForm();
                    handleClose();
                }}
                >
                Cancel
                </Button>
        <Button type="submit" form="fdrForm" variant="contained">
          Save FDR
        </Button>
      </DialogActions>

    </Dialog>
  );
};

export default FdrCreateModal;